import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { notificationRepository } from "../repositories";
import { notificationSoundService } from "../services/notification-sound.service";
const NOTIFICATION_PAGE_SIZE = 25;
export const useNotificationStore = defineStore("notifications", () => {
    const notifications = ref([]);
    const preferences = ref([]);
    const unreadCount = ref(0);
    const unreadCountStale = ref(false);
    const hasHydratedUnreadCount = ref(false);
    const activeStatus = ref("all");
    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const isUpdating = ref(false);
    const isLoadingPreferences = ref(false);
    const isSavingPreferences = ref(false);
    const hasMore = ref(false);
    const error = ref(null);
    const preferencesError = ref(null);
    const unreadNotifications = computed(() => notifications.value.filter((notification) => notification.status === "unread"));
    const visibleNotifications = computed(() => activeStatus.value === "unread"
        ? unreadNotifications.value
        : notifications.value);
    async function fetchNotifications(status = activeStatus.value) {
        activeStatus.value = status;
        isLoading.value = true;
        hasMore.value = false;
        error.value = null;
        try {
            const nextNotifications = await notificationRepository.list({
                status,
                limit: NOTIFICATION_PAGE_SIZE,
                offset: 0,
            });
            notifications.value = nextNotifications;
            hasMore.value = nextNotifications.length === NOTIFICATION_PAGE_SIZE;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "No se pudieron cargar las notificaciones";
        }
        finally {
            isLoading.value = false;
        }
    }
    async function loadMore() {
        if (isLoading.value || isLoadingMore.value || !hasMore.value)
            return;
        isLoadingMore.value = true;
        error.value = null;
        try {
            const nextNotifications = await notificationRepository.list({
                status: activeStatus.value,
                limit: NOTIFICATION_PAGE_SIZE,
                offset: notifications.value.length,
            });
            const seenIds = new Set(notifications.value.map((item) => item.id));
            notifications.value = [
                ...notifications.value,
                ...nextNotifications.filter((item) => !seenIds.has(item.id)),
            ];
            hasMore.value = nextNotifications.length === NOTIFICATION_PAGE_SIZE;
        }
        catch (err) {
            error.value =
                err instanceof Error ? err.message : "No se pudieron cargar más notificaciones";
        }
        finally {
            isLoadingMore.value = false;
        }
    }
    async function fetchUnreadCount(options = {}) {
        const previousUnreadCount = unreadCount.value;
        try {
            const nextUnreadCount = (await notificationRepository.unreadCount()).count;
            unreadCount.value = nextUnreadCount;
            unreadCountStale.value = false;
            if (hasHydratedUnreadCount.value &&
                options.playSound === true &&
                nextUnreadCount > previousUnreadCount) {
                notificationSoundService.playNotificationIfAllowed({
                    previousUnreadCount,
                    nextUnreadCount,
                    currentPath: options.currentPath,
                    isTenantAdmin: options.isTenantAdmin,
                });
            }
            hasHydratedUnreadCount.value = true;
        }
        catch {
            unreadCountStale.value = hasHydratedUnreadCount.value;
        }
    }
    async function refresh(options = {}) {
        await Promise.all([
            fetchNotifications(activeStatus.value),
            fetchUnreadCount(options),
        ]);
    }
    async function markRead(id) {
        isUpdating.value = true;
        const previousNotifications = [...notifications.value];
        const previousUnreadCount = unreadCount.value;
        const markReadAt = new Date().toISOString();
        const nextNotifications = notifications.value.map((notification) => notification.id === id
            ? {
                ...notification,
                status: "read",
                read_at: notification.read_at ?? markReadAt,
                seen_at: notification.seen_at ?? markReadAt,
            }
            : notification);
        notifications.value =
            activeStatus.value === "unread"
                ? nextNotifications.filter((notification) => notification.id !== id)
                : nextNotifications;
        unreadCount.value = Math.max(0, unreadCount.value - 1);
        try {
            await notificationRepository.markRead(id);
        }
        catch (err) {
            notifications.value = previousNotifications;
            unreadCount.value = previousUnreadCount;
            error.value =
                err instanceof Error ? err.message : "No se pudo marcar la notificación";
            throw err;
        }
        finally {
            isUpdating.value = false;
        }
    }
    async function readAll() {
        isUpdating.value = true;
        const previousNotifications = [...notifications.value];
        const previousUnreadCount = unreadCount.value;
        const now = new Date().toISOString();
        notifications.value = notifications.value.map((notification) => ({
            ...notification,
            status: "read",
            read_at: notification.read_at ?? now,
            seen_at: notification.seen_at ?? now,
        }));
        unreadCount.value = 0;
        try {
            await notificationRepository.readAll();
            await fetchUnreadCount();
        }
        catch (err) {
            notifications.value = previousNotifications;
            unreadCount.value = previousUnreadCount;
            error.value =
                err instanceof Error ? err.message : "No se pudieron marcar las notificaciones";
            throw err;
        }
        finally {
            isUpdating.value = false;
        }
    }
    async function fetchPreferences() {
        isLoadingPreferences.value = true;
        preferencesError.value = null;
        try {
            preferences.value = await notificationRepository.getPreferences();
        }
        catch (err) {
            preferencesError.value =
                err instanceof Error
                    ? err.message
                    : "No se pudieron cargar las preferencias";
            throw err;
        }
        finally {
            isLoadingPreferences.value = false;
        }
    }
    async function savePreferences(nextPreferences) {
        isSavingPreferences.value = true;
        preferencesError.value = null;
        try {
            preferences.value = await notificationRepository.patchPreferences({
                preferences: nextPreferences,
            });
        }
        catch (err) {
            preferencesError.value =
                err instanceof Error
                    ? err.message
                    : "No se pudieron guardar las preferencias";
            throw err;
        }
        finally {
            isSavingPreferences.value = false;
        }
    }
    async function handleRealtimeInvalidation() {
        await refresh({ playSound: true });
    }
    function $reset() {
        notifications.value = [];
        preferences.value = [];
        unreadCount.value = 0;
        unreadCountStale.value = false;
        hasHydratedUnreadCount.value = false;
        activeStatus.value = "all";
        isLoading.value = false;
        isLoadingMore.value = false;
        isUpdating.value = false;
        isLoadingPreferences.value = false;
        isSavingPreferences.value = false;
        hasMore.value = false;
        error.value = null;
        preferencesError.value = null;
    }
    return {
        notifications,
        preferences,
        unreadCount,
        unreadCountStale,
        activeStatus,
        isLoading,
        isLoadingMore,
        isUpdating,
        isLoadingPreferences,
        isSavingPreferences,
        hasMore,
        error,
        preferencesError,
        unreadNotifications,
        visibleNotifications,
        fetchNotifications,
        loadMore,
        fetchUnreadCount,
        refresh,
        markRead,
        readAll,
        fetchPreferences,
        savePreferences,
        handleRealtimeInvalidation,
        $reset,
    };
});
